import React from 'react';
import {useOutlet} from 'reconnect.js';
import styled from 'styled-components';
import * as User from 'rev.sdk.js/Actions/User';
import Config from '../../../data.json';
import AdminLayout from '../AdminLayout';
import SiteNavBar from '../SiteNavBar';
import ProfileLayout from '../ProfileLayout';
import SiteFooter from '../SiteFooter';
import CheckoutLayout from '../CheckoutLayout';
import useFacebookPixel from '../../Hooks/useFacebookPixel';
import useGoogleAnalytics from '../../Hooks/useGoogleAnalytics';
import useLayoutInit from '../../Hooks/useLayoutInit';
import PleaseWait from '../PleaseWait';
import useIsInApp from 'rev.sdk.js/Hooks/useIsInApp';
import useSiteNavBar from '../../Hooks/useSiteNavBar';
import useSiteFooter from '../../Hooks/useSiteFooter';
import * as CustomLayout from '../../Utils/CustomLayout';

function Layout(props) {
  const {children, location} = props;
  const [dimension] = useOutlet('dimension');
  const isInApp = useIsInApp();
  const [initialized, setInitialized] = React.useState(false);

  useFacebookPixel({pixelId: Config.pixelId});
  useGoogleAnalytics({gaId: Config.gaId, gtmId: Config.gtmId});

  const {extraElement} = useLayoutInit(props);
  const {showNavbar} = useSiteNavBar({
    location: props.location,
    pageContext: props.pageContext,
  });
  const {showFooter} = useSiteFooter({
    location: props.location,
    pageContext: props.pageContext,
  });

  React.useEffect(() => {
    const isAdmin = location.pathname.indexOf('admin') !== -1;

    const initialize = async (isAdmin) => {
      let result = false;

      try {
        result = await User.autoLogin({admin: isAdmin});
      } catch (ex) {
        console.log('autoLogin ex', ex);
        User.logout();
      }

      if (Config.allowAnonymousCheckout && !result) {
        await User.tempLogin();
      }

      setInitialized(true);
    };

    if (!initialized) {
      initialize(isAdmin);
    }
  }, [location.pathname, initialized]);

  React.useEffect(() => {
    const isAdmin = location.pathname.indexOf('admin') !== -1;

    if (initialized && isAdmin) {
      let timer = setInterval(async () => {
        console.log('auto refresh access token');
        try {
          await User.autoLogin({admin: isAdmin});
        } catch (ex) {
          console.log('autoLogin ex', ex);
          User.logout();
        }
      }, 7080000); // 1 hour 58 minute

      return () => clearInterval(timer);
    }
  }, [location.pathname, initialized]);

  if (location.pathname.indexOf('admin') !== -1) {
    if (!initialized) {
      return <PleaseWait title="自動登入中" />;
    }

    return (
      <>
        {CustomLayout.renderHtmlHelmet({location})}
        <AdminLayout location={location}>{children}</AdminLayout>
      </>
    );
  } else if (location.pathname.indexOf('profile') !== -1) {
    if (!initialized) {
      return <PleaseWait title="自動登入中" />;
    }

    return (
      <>
        {CustomLayout.renderHtmlHelmet({location})}
        <SiteNavBar {...props} />
        <ProfileLayout style={{flex: 1}} location={location}>
          {children}
        </ProfileLayout>
        <SiteFooter {...props} />
      </>
    );
  } else if (location.pathname.indexOf('checkout') !== -1) {
    if (!initialized) {
      return <PleaseWait title="自動登入中" />;
    }

    return (
      <>
        {CustomLayout.renderHtmlHelmet({location})}
        {!isInApp && <SiteNavBar {...props} />}
        <CheckoutLayout style={{flex: 1}} location={location}>
          {children}
        </CheckoutLayout>
        {!isInApp && <SiteFooter {...props} />}
      </>
    );
  }

  return (
    <>
      {CustomLayout.renderHtmlHelmet({location})}
      <Wrapper dimension={dimension}>
        {showNavbar && <SiteNavBar {...props} />}
        <div style={{flex: 1}}>{children}</div>
        {showFooter && <SiteFooter {...props} />}
        {extraElement}
      </Wrapper>
    </>
  );
}

const Wrapper = styled.div`
  --contentMaxWith: 1340px;
  --topNavBarHeight: 90px;
  --basePadding: 20px;
  --contentTextSize: 16px;
  --titleTextSize: ${(props) =>
    props.dimension.rwd === 'desktop' ? '20px' : '16px'};
  --contentTextColor: #434343;
  --contentTextLetterSpacing: 2px;
  --primaryColor: #b02b4c;
  --primaryFontColor: #434343;
  --secondFontColor: #6b6e6a;
  --sectionPadding: 50px 50px;
  --sectionMobilePadding: 40px 20px;

  background-color: #f6f6f6;
  .centered {
    margin: 0 auto;
    text-align: center;
  }

  .line {
    background-color: #cccccc;
    height: 1px;
  }

  .row {
    display: flex;
    flex-direction: row;
  }

  .section {
    max-width: var(--contentMaxWith);
    margin: 0 auto;
    padding: ${(props) =>
      props.dimension.rwd === 'desktop'
        ? 'var(--sectionPadding)'
        : 'var(--sectionMobilePadding)'};
  }

  .constraint {
    max-width: var(--contentMaxWith);
    margin: 0 auto;
    padding: ${(props) =>
      props.dimension.rwd === 'desktop'
        ? 'var(--sectionPadding)'
        : 'var(--sectionMobilePadding)'};
  }

  .light-bkg {
    background-color: #fbfbfb;
  }

  .title {
    font-family: 'Noto Serif TC';
    font-weight: 450;
    white-space: break-spaces;
    font-size: ${(props) => (props.dimension.rwd === 'desktop' ? 24 : 16)}px;
    color: var(--primaryFontColor);
    letter-spacing: 3px;
  }

  .content {
    font-size: ${(props) => (props.dimension.rwd === 'desktop' ? 16 : 14)}px;
    letter-spacing: 2px;
    line-height: 26px;
    white-space: break-spaces;
  }

  .primary-color {
    color: var(--primaryColor);
  }

  .subtitle {
    color: var(--primaryColor);
    font-size: ${(props) => (props.dimension.rwd === 'desktop' ? 14 : 10)}px;
    letter-spacing: 2px;
    font-weight: bold;
  }

  .grid {
    display: flex;
    flex-direction: ${(props) =>
      props.dimension.rwd === 'mobile' ? 'column' : 'row'};
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .grid-item-large {
    display: flex;
    flex-direction: ${(props) =>
      props.dimension.rwd === 'desktop' ? 'row' : 'column'};
    flex: 1;
    margin-top: 30px;
    cursor: pointer;
  }

  .grid-item {
    display: flex;
    flex-direction: column;
    margin-top: 30px;
    cursor: pointer;
    width: ${(props) => {
      if (props.dimension.rwd === 'mobile') {
        return `none`;
      } else if (props.dimension.rwd === 'desktop') {
        return `${(1240 - 100 - 90) / 3}px`;
      } else {
        return `${(props.dimension.innerWidth - 40 - 30) / 3}px`;
      }
    }};
  }

  .grid-item-img {
    height: ${(props) => {
      if (props.dimension.rwd === 'desktop') {
        return '250px';
      } else if (props.dimension.rwd === 'pad') {
        return `${(((props.dimension.innerWidth - 60) / 3) * 5) / 7}px`;
      } else {
        return `${((props.dimension.innerWidth - 40) * 5) / 7}px`;
      }
    }}; // keep 7:5 ratio
    max-width: 400px;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: ${(props) =>
      props.dimension.rwd === 'desktop' ? '20px' : '10px'};
  }

  .grid-item-img-large {
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    flex-basis: ${(props) =>
      props.dimension.rwd === 'desktop' ? '370px' : '200px'};
    min-width: ${(props) =>
      props.dimension.rwd === 'desktop' ? '370px' : '200px'};
    height: 277px; // to make ratio 4:3
    border-radius: 10px;
  }

  .grid-context-container {
    padding: ${(props) =>
      props.dimension.rwd === 'desktop' ? '40px 50px 50px 50px' : '10px'};
  }

  .grid-item-date {
    color: var(--primaryColor);
    font-size: 13px;
    letter-spacing: 2px;
  }
  .grid-item-title {
    flex: 1;
    font-weight: 500;
    font-size: var(--titleTextSize);
    color: var(--primaryColor);
    letter-spacing: 2.57px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .grid-item-outline {
    flex: 1;
    font-size: var(--contentTextSize);
    letter-spacing: 2px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    white-space: break-spaces;
    min-height: 85px;
    line-height: 28px;
  }

  .tab-wrapper {
    display: flex;
    padding: 20px 30px;
    border-radius: 8px;
    background-color: white;
    flex-wrap: wrap;
    overflow-x: scroll;
    justify-content: center;
  }

  .tab {
    margin: 3px 15px;
    font-size: var(--contentTextSize);
    white-space: nowrap;
    cursor: pointer;
    letter-spacing: 1px;
    min-width: 100px;
    text-align: center;
  }

  .selected-tab {
    color: var(--primaryColor);
    font-size: var(--contentTextSize);
    font-weight: bold;
    border-bottom: 0.5px solid var(--primaryColor);
  }

  .scroll-constrain {
    overflow-x: auto;
  }

  .scroll-wrapper {
    display: -webkit-box;
  }
  .scroll-item {
    display: flex;
    flex-direction: column;
    padding: 10px;
    margin-right: 20px;
  }
`;

export default Layout;
